import { useEffect, useRef, useState } from "react";
import { BASE_API_URL } from "../../Components/config";
import { getFreshAccessToken, isTokenExpired } from "../../Components/auth/session";

const API = `${BASE_API_URL}/api/v1/session-rooms`;

/**
 * The room's live stream.
 *
 * Server-sent events: the server pushes what happened instead of the client
 * asking. `EventSource` handles reconnection itself, which is most of why SSE
 * is worth choosing over a socket here.
 *
 * It cannot send an Authorization header — EventSource has no way to set one —
 * so the token goes in the query string. That is fine over HTTPS for a
 * short-lived access token, and it is the same token the other calls carry.
 *
 * Deliberately additive: the room keeps a slow poll underneath, so if the
 * stream is unavailable — a proxy that buffers, a multi-worker deployment,
 * a network that blocks long responses — the room degrades to what it did
 * before rather than going quiet.
 */
export function useRoomStream({ sessionGuid, subRoomGuid = null, enabled = true, handlers }) {
  const [connected, setConnected] = useState(false);
  // Bumped to open a new stream after the old one was turned away.
  const [reopen, setReopen] = useState(0);
  const callbacks = useRef(handlers);
  callbacks.current = handlers;

  useEffect(() => {
    if (!enabled || !sessionGuid) return undefined;

    const token = localStorage.getItem("accessToken");
    // A sub-room has its own stream, carrying only its board.
    const path = subRoomGuid ? `${sessionGuid}/sub-rooms/${subRoomGuid}` : sessionGuid;
    const source = new EventSource(`${API}/${path}/stream?token=${encodeURIComponent(token || "")}`);

    const on = (name, fn) => {
      const wrapped = (e) => {
        try {
          fn(JSON.parse(e.data));
        } catch (err) {
          console.error(`Malformed ${name} event`, err);
        }
      };
      source.addEventListener(name, wrapped);
      return () => source.removeEventListener(name, wrapped);
    };

    const offs = [
      on("ready", () => setConnected(true)),
      on("message.created", (e) => callbacks.current?.onMessage?.(e.message)),
      on("messages.answered", (e) => callbacks.current?.onAnswered?.(e.message_guids)),
      on("board.saved", (e) => callbacks.current?.onBoard?.(e.board, e.by)),
      on("board.ops", (e) => callbacks.current?.onBoardOps?.(e.ops, e.by)),
      on("board.live", (e) => callbacks.current?.onBoardLive?.(e.live, e.payload, e.by)),
      on("subrooms.updated", (e) => callbacks.current?.onSubRooms?.(e.rooms)),
      on("activity.updated", (e) => callbacks.current?.onActivity?.(e.activity_guid)),
      on("session.ended", () => callbacks.current?.onSessionEnded?.()),
      on("attendance.joined", () => callbacks.current?.onArrived?.()),
    ];

    source.onerror = () => {
      // EventSource retries on its own; this only reflects the current state
      // so the room can say whether it is live.
      setConnected(false);
      // Except when the server refuses it outright, and then it gives up. If
      // that is because the token in the URL has run out, open a new stream
      // with a fresh one; any other refusal is left to the slow poll.
      if (source.readyState === EventSource.CLOSED && isTokenExpired(token)) {
        getFreshAccessToken().then((fresh) => {
          if (fresh && fresh !== token) setReopen((n) => n + 1);
        });
      }
    };

    return () => {
      offs.forEach((off) => off());
      source.close();
      setConnected(false);
    };
  }, [enabled, sessionGuid, subRoomGuid, reopen]);

  return { connected };
}
